import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Users, TrendingUp, Loader2, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BarRow = ({ label, value, max, color = 'bg-emerald-500' }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-sm text-gray-700 w-44 truncate font-medium" title={label}>{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
        <div className={`${color} h-full rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 1.5)}%` }} />
      </div>
      <span className="text-sm font-bold text-gray-800 w-10 text-right">{value}</span>
    </div>
  );
};

const ReportSection = ({ title, data, color, icon, maxItems = 15 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data || Object.keys(data).length === 0) return null;
  const entries = Object.entries(data);
  const maxVal = Math.max(...entries.map(([, v]) => v));
  const visible = expanded ? entries : entries.slice(0, maxItems);
  const total = entries.reduce((s, [, v]) => s + v, 0);

  return (
    <Card className="p-5" data-testid={`family-report-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-bold text-sm text-gray-900">{title}</h3>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{entries.length} groups, {total} total</span>
        </div>
      </div>
      <div className="space-y-0.5">
        {visible.map(([label, value]) => (
          <BarRow key={label} label={label} value={value} max={maxVal} color={color} />
        ))}
      </div>
      {entries.length > maxItems && (
        <button onClick={() => setExpanded(!expanded)} className="mt-3 text-xs text-emerald-600 hover:text-emerald-800 font-medium">
          {expanded ? 'Show less' : `Show all ${entries.length} entries`}
        </button>
      )}
    </Card>
  );
};

const SummaryCard = ({ label, value, sub, color }) => (
  <Card className={`p-4 ${color}`}>
    <p className="text-xs font-medium opacity-70">{label}</p>
    <p className="text-3xl font-bold">{value ?? 0}</p>
    {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
  </Card>
);

export default function FamilyReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/family-registrations/reports`, { headers })
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  }, []);

  const exportReport = () => {
    if (!data) return;
    const rows = [];
    const addSection = (section, obj) => {
      if (!obj) return;
      Object.entries(obj).forEach(([k, v]) => {
        if (typeof v === 'object' && v !== null) {
          Object.entries(v).forEach(([label, count]) => rows.push({ Section: section, Report: k, Category: label, Count: count }));
        }
      });
    };
    addSection('Families', data.families);
    addSection('All Members', data.all);
    addSection('Male Members', data.male);
    addSection('Female Members', data.female);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Family Reports');
    XLSX.writeFile(wb, `family_reports_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Report exported');
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!data) return <div className="p-6 text-center text-gray-500">No report data available.</div>;

  const { families, all, male, female } = data;
  const ratio = male.total && female.total ? `${(male.total / female.total).toFixed(2)} : 1` : 'N/A';

  return (
    <div className="p-6" data-testid="family-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-emerald-600" /> Family Registration Dashboard
        </h1>
        <Button variant="outline" size="sm" onClick={exportReport} data-testid="family-reports-export">
          <Download className="w-4 h-4 mr-1" /> Export Report
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <SummaryCard label="Total Families" value={families.total} color="bg-emerald-50 text-emerald-700" />
        <SummaryCard label="Total Persons" value={all.total} color="bg-blue-50 text-blue-700" />
        <SummaryCard label="Male Members" value={male.total} color="bg-sky-50 text-sky-700" />
        <SummaryCard label="Female Members" value={female.total} color="bg-pink-50 text-pink-700" />
        <SummaryCard label="Male : Female" value={ratio} color="bg-purple-50 text-purple-700" />
      </div>

      {/* Family Reports */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <Users className="w-5 h-5 text-emerald-600" /> Family Reports
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <ReportSection title="Families by Status" data={families.byStatus} color="bg-emerald-500" icon={<div className="w-3 h-3 rounded-full bg-emerald-500" />} />
        <ReportSection title="Families by City" data={families.byPlace} color="bg-teal-500" icon={<div className="w-3 h-3 rounded-full bg-teal-500" />} />
        <ReportSection title="Families by Nukh" data={families.byNukh} color="bg-green-500" icon={<div className="w-3 h-3 rounded-full bg-green-500" />} />
      </div>

      {/* All Members */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <TrendingUp className="w-5 h-5 text-blue-600" /> All Members ({all.total})
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <ReportSection title="By Place" data={all.byPlace} color="bg-blue-500" icon={<div className="w-3 h-3 rounded-full bg-blue-500" />} />
        <ReportSection title="By Nukh" data={all.byNukh} color="bg-indigo-500" icon={<div className="w-3 h-3 rounded-full bg-indigo-500" />} />
        <ReportSection title="By Education" data={all.byEducation} color="bg-violet-500" icon={<div className="w-3 h-3 rounded-full bg-violet-500" />} />
        <ReportSection title="By Occupation" data={all.byOccupation} color="bg-cyan-500" icon={<div className="w-3 h-3 rounded-full bg-cyan-500" />} />
        <ReportSection title="By Marital Status" data={all.byMaritalStatus} color="bg-amber-500" icon={<div className="w-3 h-3 rounded-full bg-amber-500" />} />
        <ReportSection title="By Age Group" data={all.byAgeGroup} color="bg-orange-500" icon={<div className="w-3 h-3 rounded-full bg-orange-500" />} />
      </div>

      {/* Male Breakdown */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <span className="w-5 h-5 rounded-full bg-sky-500 flex items-center justify-center text-white text-xs font-bold">M</span> Male Members ({male.total})
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <ReportSection title="Males by Place" data={male.byPlace} color="bg-sky-500" icon={<div className="w-3 h-3 rounded-full bg-sky-500" />} />
        <ReportSection title="Males by Nukh" data={male.byNukh} color="bg-blue-400" icon={<div className="w-3 h-3 rounded-full bg-blue-400" />} />
        <ReportSection title="Males by Education" data={male.byEducation} color="bg-indigo-400" icon={<div className="w-3 h-3 rounded-full bg-indigo-400" />} />
        <ReportSection title="Males by Occupation" data={male.byOccupation} color="bg-sky-400" icon={<div className="w-3 h-3 rounded-full bg-sky-400" />} />
        <ReportSection title="Males by Marital Status" data={male.byMaritalStatus} color="bg-blue-300" icon={<div className="w-3 h-3 rounded-full bg-blue-300" />} />
        <ReportSection title="Males by Age Group" data={male.byAgeGroup} color="bg-sky-300" icon={<div className="w-3 h-3 rounded-full bg-sky-300" />} />
      </div>

      {/* Female Breakdown */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <span className="w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center text-white text-xs font-bold">F</span> Female Members ({female.total})
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <ReportSection title="Females by Place" data={female.byPlace} color="bg-pink-500" icon={<div className="w-3 h-3 rounded-full bg-pink-500" />} />
        <ReportSection title="Females by Nukh" data={female.byNukh} color="bg-rose-500" icon={<div className="w-3 h-3 rounded-full bg-rose-500" />} />
        <ReportSection title="Females by Education" data={female.byEducation} color="bg-fuchsia-500" icon={<div className="w-3 h-3 rounded-full bg-fuchsia-500" />} />
        <ReportSection title="Females by Occupation" data={female.byOccupation} color="bg-pink-400" icon={<div className="w-3 h-3 rounded-full bg-pink-400" />} />
        <ReportSection title="Females by Marital Status" data={female.byMaritalStatus} color="bg-rose-400" icon={<div className="w-3 h-3 rounded-full bg-rose-400" />} />
        <ReportSection title="Females by Age Group" data={female.byAgeGroup} color="bg-fuchsia-400" icon={<div className="w-3 h-3 rounded-full bg-fuchsia-400" />} />
      </div>
    </div>
  );
}
